import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { sellerCreatedBuyerService } from "@/services/sellerCreatedBuyerService";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PURCHASING_TYPES, CLOSE_TIMES } from "@/constants/buyerFormOptions";
import { usStates } from "@/utils/states";
import { ScrollArea } from "@/components/ui/scroll-area";
import { z } from "zod";

interface EditManualBuyerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyer: any;
  onBuyerUpdated?: () => void;
}

const manualBuyerSchema = z.object({
  fullName: z.string().min(1, "Full name is required").max(100),
  companyName: z.string().max(100).optional(),
  email: z.string().email("Invalid email address").max(255).optional().or(z.literal('')),
  phoneNumber: z.string().min(1, "Phone number is required"),
  dealsAnnually: z.number().min(0, "Must be 0 or greater"),
  preferredMarkets: z.array(z.string()).min(1, "Select at least one market"),
  purchasingType: z.array(z.string()).min(1, "Select at least one purchasing type"),
  averageCloseTime: z.string().optional(),
  notes: z.string().max(500).optional(),
});

export const EditManualBuyerDialog = ({ open, onOpenChange, buyer, onBuyerUpdated }: EditManualBuyerDialogProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phoneNumber: '',
    dealsAnnually: 0,
    preferredMarkets: [] as string[],
    purchasingType: [] as string[],
    averageCloseTime: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    isPrivate: true,
    notes: ''
  });

  // Initialize form data when buyer changes
  useEffect(() => {
    if (buyer) {
      setFormData({
        fullName: buyer.full_name || '',
        companyName: buyer.company_name || '',
        email: buyer.email || '',
        phoneNumber: buyer.phone_number || '',
        dealsAnnually: buyer.deals_annually || 0,
        preferredMarkets: Array.isArray(buyer.preferred_markets) ? buyer.preferred_markets : [],
        purchasingType: Array.isArray(buyer.purchasing_type) ? buyer.purchasing_type : [],
        averageCloseTime: buyer.average_close_time || '',
        address: buyer.address || '',
        city: buyer.city || '',
        state: buyer.state || '',
        zipCode: buyer.zip_code || '',
        isPrivate: buyer.is_private ?? true,
        notes: buyer.notes || ''
      });
      setStep(1);
      setErrors({});
    }
  }, [buyer]);

  const validateStep = (stepNumber: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (stepNumber === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required";
      }
      if (formData.dealsAnnually < 0) newErrors.dealsAnnually = "Must be 0 or greater";
    } else if (stepNumber === 2) {
      if (formData.purchasingType.length === 0) {
        newErrors.purchasingType = "Select at least one purchasing type";
      }
      if (formData.preferredMarkets.length === 0) {
        newErrors.preferredMarkets = "Select at least one market";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    if (!buyer?.id) return;

    setIsSubmitting(true);
    
    try {
      const validated = manualBuyerSchema.parse(formData);
      
      const { success, error } = await sellerCreatedBuyerService.updateBuyer(buyer.id, {
        sellerId: buyer.seller_id,
        fullName: validated.fullName,
        companyName: validated.companyName,
        email: validated.email,
        phoneNumber: validated.phoneNumber,
        dealsAnnually: validated.dealsAnnually,
        preferredMarkets: validated.preferredMarkets,
        purchasingType: validated.purchasingType,
        averageCloseTime: validated.averageCloseTime,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        isPrivate: true,
        notes: validated.notes
      });
      
      if (!success) {
        toast({
          title: "Error",
          description: error || "Failed to update buyer",
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Success!",
        description: "Buyer information updated successfully",
      });
      
      onBuyerUpdated?.();
      onOpenChange(false);
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMarket = (market: string) => {
    setFormData(prev => ({
      ...prev,
      preferredMarkets: prev.preferredMarkets.includes(market)
        ? prev.preferredMarkets.filter(m => m !== market)
        : [...prev.preferredMarkets, market]
    }));
  };

  const togglePurchasingType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      purchasingType: prev.purchasingType.includes(type)
        ? prev.purchasingType.filter(t => t !== type)
        : [...prev.purchasingType, type]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            Edit Buyer Information - {step === 1 ? 'Personal Info' : 'Preferences'} [{step}/2]
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Update the buyer's personal information"}
            {step === 2 && "Update their investment preferences"}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="John Smith"
                />
                {errors.fullName && <p className="text-sm text-destructive mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  placeholder="ABC Investments"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@abc.com"
                />
                {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <PhoneInput
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(value) => setFormData({ ...formData, phoneNumber: value })}
                  showValidation={true}
                />
                {errors.phoneNumber && <p className="text-sm text-destructive mt-1">{errors.phoneNumber}</p>}
              </div>

              <div>
                <Label htmlFor="dealsAnnually">Deals Completed Annually *</Label>
                <Input
                  id="dealsAnnually"
                  type="number"
                  value={formData.dealsAnnually}
                  onChange={(e) => setFormData({ ...formData, dealsAnnually: parseInt(e.target.value) || 0 })}
                  min="0"
                />
                {errors.dealsAnnually && <p className="text-sm text-destructive mt-1">{errors.dealsAnnually}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St"
                  />
                </div>

                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Chicago"
                  />
                </div>

                <div>
                  <Label htmlFor="state">State</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                    <SelectTrigger id="state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {usStates.map((state) => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="zipCode">ZIP Code</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    placeholder="60601"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Investment Preferences */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="text-base mb-3 block">Purchasing Types *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {PURCHASING_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={formData.purchasingType.includes(type)}
                        onCheckedChange={() => togglePurchasingType(type)}
                      />
                      <Label
                        htmlFor={`type-${type}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {type}
                      </Label>
                    </div>
                  ))}
                </div>
                {errors.purchasingType && (
                  <p className="text-sm text-destructive mt-2">{errors.purchasingType}</p>
                )}
              </div>

              <div>
                <Label htmlFor="averageCloseTime">Average Time Needed to Close</Label>
                <Select
                  value={formData.averageCloseTime}
                  onValueChange={(value) => setFormData({ ...formData, averageCloseTime: value })}
                >
                  <SelectTrigger id="averageCloseTime">
                    <SelectValue placeholder="Select timeframe" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLOSE_TIMES.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-base mb-3 block">Preferred Markets *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md">
                  {usStates.map((state) => (
                    <div key={state.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`market-${state.id}`}
                        checked={formData.preferredMarkets.includes(state.name)}
                        onCheckedChange={() => toggleMarket(state.name)}
                      />
                      <Label
                        htmlFor={`market-${state.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {state.name}
                      </Label>
                    </div>
                  ))}
                </div>
                {errors.preferredMarkets && (
                  <p className="text-sm text-destructive mt-2">{errors.preferredMarkets}</p>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional information about this buyer..."
                  className="min-h-[80px]"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.notes.length}/500 characters
                </p>
              </div>
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-between pt-4 border-t">
          {step === 1 ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleNext}>
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleBack}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update Buyer"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
